import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Phone, Smartphone, Wallet } from 'lucide-react';
import { useDashboardUi } from '@/components/layout/UserLayout';
import { PinVerificationModal } from '@/components/user/PinVerificationModal';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import {
  AIRTIME_NETWORKS,
  detectAirtimeNetwork,
  getAirtimeNetworkMeta,
} from '@/constants/airtimeNetworks';
import { useUserAuth } from '@/context/UserAuthContext';
import { dataService, getApiErrorMessage } from '@/services';
import type { AirtimeNetwork } from '@/types/airtime';
import type { DataPlanOption } from '@/types/data';
import { cn } from '@/utils/cn';
import { notify } from '@/utils/notify';

const NETWORK_ORDER: AirtimeNetwork[] = ['MTN', 'AIRTEL', 'GLO', '9MOBILE'];
const PLAN_TYPE_ORDER = ['DATA SHARE', 'GIFTING', 'CG', 'DATA', 'CORPORATE', 'AWOOF', 'AWOOF AIRTIME'];

const VALIDITY_PERIODS = [
  { key: 'DAILY', label: 'Daily', minDays: 1, maxDays: 1 },
  { key: 'WEEKLY', label: 'Weekly', minDays: 2, maxDays: 7 },
  { key: 'MONTHLY', label: 'Monthly', minDays: 8, maxDays: 31 },
  { key: 'BI_MONTHLY', label: 'BI-MONTHLY', minDays: 32, maxDays: Number.POSITIVE_INFINITY },
] as const;

/** Fixed viewport height for the plan grid (matches Otobill h-48). */
const PLAN_GRID_HEIGHT_PX = 192;

type ValidityKey = (typeof VALIDITY_PERIODS)[number]['key'];

function parseValidityDays(validity: string): number {
  const raw = validity.trim().toLowerCase();
  if (!raw) return 30;
  if (raw.includes('week')) return 7;
  if (raw.includes('month')) return 30;
  const match = raw.match(/(\d+)/);
  return match ? Number(match[1]) : 30;
}

function pickDefaultNetwork(available: AirtimeNetwork[]): AirtimeNetwork {
  for (const network of NETWORK_ORDER) {
    if (available.includes(network)) return network;
  }
  return available[0] ?? 'MTN';
}

function pickDefaultPlanType(types: string[]): string {
  for (const type of PLAN_TYPE_ORDER) {
    if (types.includes(type)) return type;
  }
  return types[0] ?? '';
}

function sortNetworks(available: AirtimeNetwork[]): AirtimeNetwork[] {
  return NETWORK_ORDER.filter((network) => available.includes(network));
}

export function DataPage() {
  const { user, persistUser } = useUserAuth();
  const { wallet, loadWallet } = useDashboardUi();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dataPlansRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(true);
  const [providerReady, setProviderReady] = useState(true);
  const [allPlans, setAllPlans] = useState<DataPlanOption[]>([]);
  const [planTypesByNetwork, setPlanTypesByNetwork] = useState<Record<string, string[]>>({});
  const [availableNetworks, setAvailableNetworks] = useState<AirtimeNetwork[]>([]);

  const [selectedNetwork, setSelectedNetwork] = useState<AirtimeNetwork>('MTN');
  const [selectedPlanType, setSelectedPlanType] = useState('');
  const [selectedValidity, setSelectedValidity] = useState<ValidityKey | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [networkManual, setNetworkManual] = useState(false);
  const [showNetworkDropdown, setShowNetworkDropdown] = useState(false);

  const [quoteLoading, setQuoteLoading] = useState(false);
  const [amountToPay, setAmountToPay] = useState<number | null>(null);

  const [showPinModal, setShowPinModal] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);

  const walletBalance = wallet?.balance ?? user?.walletBalance ?? 0;
  const selectedMeta = getAirtimeNetworkMeta(selectedNetwork);
  const orderedNetworks = useMemo(() => sortNetworks(availableNetworks), [availableNetworks]);

  const planTypes = planTypesByNetwork[selectedNetwork] ?? [];

  const typeFilteredPlans = useMemo(() => {
    return allPlans
      .filter((plan) => {
        if (plan.network !== selectedNetwork) return false;
        if (selectedPlanType && plan.planType !== selectedPlanType) return false;
        return true;
      })
      .sort((a, b) => a.price - b.price || a.planName.localeCompare(b.planName));
  }, [allPlans, selectedNetwork, selectedPlanType]);

  const availableValidityPeriods = useMemo(() => {
    return VALIDITY_PERIODS.filter((period) =>
      typeFilteredPlans.some((plan) => {
        const days = parseValidityDays(plan.validity);
        if (period.maxDays === Number.POSITIVE_INFINITY) return days >= period.minDays;
        return days >= period.minDays && days <= period.maxDays;
      }),
    );
  }, [typeFilteredPlans]);

  const filteredPlans = useMemo(() => {
    if (!selectedValidity) return typeFilteredPlans;
    const period = VALIDITY_PERIODS.find((p) => p.key === selectedValidity);
    if (!period) return typeFilteredPlans;
    return typeFilteredPlans.filter((plan) => {
      const days = parseValidityDays(plan.validity);
      if (period.maxDays === Number.POSITIVE_INFINITY) return days >= period.minDays;
      return days >= period.minDays && days <= period.maxDays;
    });
  }, [typeFilteredPlans, selectedValidity]);

  const selectedPlan = filteredPlans.find((p) => p.id === selectedPlanId) ?? null;

  useEffect(() => {
    const container = dataPlansRef.current;
    if (!container) return;

    const handleScroll = () => {
      const maxScrollTop = container.scrollHeight - container.clientHeight;
      setScrollPosition(container.scrollTop);
      setMaxScroll(maxScrollTop);
    };

    container.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => container.removeEventListener('scroll', handleScroll);
  }, [filteredPlans]);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dataService.getPlans();
      const data = res.data;
      const networks = sortNetworks((data?.networks ?? []) as AirtimeNetwork[]);
      const typesByNetwork = data?.planTypesByNetwork ?? {};

      setAllPlans(data?.plans ?? []);
      setPlanTypesByNetwork(typesByNetwork);
      setAvailableNetworks(networks);
      setProviderReady(Boolean(data?.configured));

      const defaultNetwork = pickDefaultNetwork(networks);
      const defaultType = pickDefaultPlanType(typesByNetwork[defaultNetwork] ?? []);
      setSelectedNetwork(defaultNetwork);
      setSelectedPlanType(defaultType);
      setSelectedValidity(null);
      setSelectedPlanId(null);
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load data plans'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadQuote = useCallback(async () => {
    if (!selectedPlan) {
      setAmountToPay(null);
      return;
    }
    setQuoteLoading(true);
    try {
      const res = await dataService.getQuote(selectedPlan.id);
      setAmountToPay(res.data?.quote?.amountToPay ?? selectedPlan.price);
    } catch {
      setAmountToPay(selectedPlan.price);
    } finally {
      setQuoteLoading(false);
    }
  }, [selectedPlan]);

  useEffect(() => {
    void loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    void loadQuote();
  }, [loadQuote]);

  useEffect(() => {
    const types = planTypesByNetwork[selectedNetwork] ?? [];
    if (types.length && !types.includes(selectedPlanType)) {
      setSelectedPlanType(pickDefaultPlanType(types));
    }
  }, [selectedNetwork, planTypesByNetwork, selectedPlanType]);

  useEffect(() => {
    if (selectedValidity && !availableValidityPeriods.some((p) => p.key === selectedValidity)) {
      setSelectedValidity(null);
    }
  }, [availableValidityPeriods, selectedValidity]);

  useEffect(() => {
    if (selectedPlanId != null && !filteredPlans.some((p) => p.id === selectedPlanId)) {
      setSelectedPlanId(null);
    }
  }, [filteredPlans, selectedPlanId]);

  useEffect(() => {
    function onClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowNetworkDropdown(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  function handleNetworkPick(network: AirtimeNetwork) {
    setSelectedNetwork(network);
    setNetworkManual(true);
    setShowNetworkDropdown(false);
    const types = planTypesByNetwork[network] ?? [];
    setSelectedPlanType(pickDefaultPlanType(types));
    setSelectedValidity(null);
    setSelectedPlanId(null);
  }

  function handlePhoneChange(value: string) {
    const numeric = value.replace(/\D/g, '').slice(0, 11);
    setPhoneNumber(numeric);
    if (numeric.length >= 4 && !networkManual) {
      const detected = detectAirtimeNetwork(numeric);
      if (detected && availableNetworks.includes(detected) && detected !== selectedNetwork) {
        setSelectedNetwork(detected);
        const types = planTypesByNetwork[detected] ?? [];
        setSelectedPlanType(pickDefaultPlanType(types));
        setSelectedValidity(null);
        setSelectedPlanId(null);
      }
    }
  }

  const canPurchase =
    providerReady &&
    Boolean(selectedPlan) &&
    phoneNumber.length === 11 &&
    user?.pinEnabled &&
    (networkManual || Boolean(detectAirtimeNetwork(phoneNumber)));

  async function handlePurchase(pin: string) {
    if (!selectedPlan) return;
    setPurchasing(true);
    try {
      const res = await dataService.purchase({
        planId: selectedPlan.id,
        phoneNumber,
        pin,
      });
      if (res.data?.user) persistUser(res.data.user);
      await loadWallet();
      notify.success(res.message || 'Data purchased successfully');
      setShowPinModal(false);
      setPhoneNumber('');
      setNetworkManual(false);
      setSelectedPlanId(null);
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Data purchase failed'));
    } finally {
      setPurchasing(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader label="Loading data plans" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-brand-navy sm:text-3xl">Buy Data</h1>
        <p className="mt-1 text-sm text-brand-muted sm:text-base">
          Data bundles for MTN, Airtel, and Glo — fixed pricing, instant delivery
        </p>
      </div>

      {!providerReady && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Data purchase is temporarily unavailable. Please try again later.
        </div>
      )}

      {!user?.pinEnabled && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Set your transaction PIN in Security before buying data.
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
        <Card className="border-gray-200 p-5 shadow-card sm:p-6 lg:p-8">
          <h2 className="mb-5 text-lg font-bold text-brand-navy">Purchase Data</h2>

          <div className="space-y-5">
            <div>
              <label htmlFor="data-phone" className="mb-2 block text-sm font-medium text-brand-navy">
                Phone number
              </label>
              <div className="flex items-center gap-3">
                <div ref={dropdownRef} className="relative shrink-0">
                  <button
                    type="button"
                    onClick={() => setShowNetworkDropdown((open) => !open)}
                    className={cn(
                      'flex h-14 w-14 items-center justify-center rounded-full border-2 bg-white transition hover:shadow-md',
                      showNetworkDropdown ? 'border-primary ring-2 ring-primary/20' : 'border-gray-200',
                    )}
                    aria-label="Select network"
                  >
                    <img
                      src={selectedMeta.logo}
                      alt={selectedMeta.label}
                      className="h-9 w-9 object-contain"
                    />
                  </button>
                  <ChevronDown
                    className={cn(
                      'pointer-events-none absolute -right-1 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-navy transition',
                      showNetworkDropdown && 'rotate-180',
                    )}
                  />

                  {showNetworkDropdown && (
                    <div className="absolute left-0 top-full z-20 mt-2 min-w-[220px] rounded-xl border border-gray-200 bg-white p-4 shadow-card">
                      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-brand-muted">
                        Select network
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        {AIRTIME_NETWORKS.filter((n) => orderedNetworks.includes(n.id)).map((network) => (
                          <button
                            key={network.id}
                            type="button"
                            onClick={() => handleNetworkPick(network.id)}
                            className={cn(
                              'flex flex-col items-center gap-2 rounded-xl border-2 p-3 transition hover:scale-[1.02]',
                              selectedNetwork === network.id
                                ? 'border-primary bg-primary/5 shadow-sm'
                                : 'border-gray-100 hover:border-gray-200',
                            )}
                          >
                            <img src={network.logo} alt={network.label} className="h-10 w-10 object-contain" />
                            <span className="text-xs font-semibold text-brand-navy">{network.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="relative min-w-0 flex-1">
                  <Phone className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-brand-muted" />
                  <input
                    id="data-phone"
                    type="tel"
                    inputMode="numeric"
                    value={phoneNumber}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    placeholder="08012345678"
                    maxLength={11}
                    className="w-full rounded-xl border border-gray-200 py-3.5 pl-12 pr-4 text-base text-brand-navy focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>
              {phoneNumber.length > 0 && phoneNumber.length !== 11 && (
                <p className="mt-2 text-sm text-red-600">Phone number must be exactly 11 digits</p>
              )}
            </div>

            {planTypes.length > 0 && (
              <div>
                <p className="mb-2.5 text-sm font-medium text-brand-navy">Data type</p>
                <div className="flex flex-wrap gap-2">
                  {planTypes.map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        setSelectedPlanType(type);
                        setSelectedValidity(null);
                        setSelectedPlanId(null);
                      }}
                      className={cn(
                        'rounded-full px-3 py-2 text-xs font-bold uppercase tracking-wide transition',
                        selectedPlanType === type
                          ? 'bg-primary text-white shadow-md'
                          : 'bg-gray-100 text-brand-navy hover:bg-gray-200',
                      )}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <p className="mb-2.5 text-sm font-medium text-brand-navy">Select data plan</p>

              {availableValidityPeriods.length > 0 && (
                <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
                  {availableValidityPeriods.map((period) => (
                    <button
                      key={period.key}
                      type="button"
                      onClick={() => {
                        setSelectedValidity((prev) => (prev === period.key ? null : period.key));
                        setSelectedPlanId(null);
                      }}
                      className={cn(
                        'shrink-0 rounded-full px-3 py-2 text-xs font-bold transition',
                        selectedValidity === period.key
                          ? 'bg-primary text-white shadow-md'
                          : 'bg-gray-100 text-brand-navy hover:bg-gray-200',
                      )}
                    >
                      {period.label}
                    </button>
                  ))}
                </div>
              )}

              {filteredPlans.length === 0 ? (
                <p className="rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-brand-muted">
                  No plans for {selectedMeta.label}
                  {selectedPlanType ? ` · ${selectedPlanType}` : ''}
                  {selectedValidity
                    ? ` · ${VALIDITY_PERIODS.find((p) => p.key === selectedValidity)?.label}`
                    : ''}
                  .
                </p>
              ) : (
                <div className="flex gap-3">
                  <div
                    ref={dataPlansRef}
                    className="grid h-48 flex-1 grid-cols-2 gap-3 overflow-y-auto scrollbar-hidden"
                    style={{ maxHeight: PLAN_GRID_HEIGHT_PX }}
                  >
                    {filteredPlans.map((plan) => {
                      const active = selectedPlanId === plan.id;
                      return (
                        <button
                          key={plan.id}
                          type="button"
                          onClick={() => setSelectedPlanId(plan.id)}
                          className={cn(
                            'rounded-lg border p-3 text-left transition',
                            active
                              ? 'border-primary bg-primary/5 shadow-md'
                              : 'border-gray-200 hover:border-gray-300 hover:shadow-sm',
                          )}
                        >
                          <div className="space-y-0.5">
                            <p className="line-clamp-2 text-sm font-medium text-brand-navy">{plan.planName}</p>
                            <p className="text-xs text-brand-muted">
                              {plan.validity ? `${plan.validity} validity` : 'Flexible validity'}
                            </p>
                            <p className="text-sm font-bold text-primary">
                              ₦{plan.price.toLocaleString()}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {filteredPlans.length > 4 && (
                    <div
                      className="flex h-48 w-8 flex-col items-center justify-center rounded-lg border border-primary/20 bg-primary/5"
                      style={{ height: PLAN_GRID_HEIGHT_PX }}
                    >
                      <button
                        type="button"
                        aria-label="Scroll plans up"
                        onClick={() => {
                          dataPlansRef.current?.scrollBy({ top: -120, behavior: 'smooth' });
                        }}
                        className="flex h-6 w-6 items-center justify-center rounded text-primary transition-colors hover:bg-primary hover:text-white"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>

                      <div
                        className="relative mx-auto my-2 w-1 flex-1 cursor-pointer rounded-full bg-primary/20"
                        onClick={(e) => {
                          if (!dataPlansRef.current || maxScroll <= 0) return;
                          const rect = e.currentTarget.getBoundingClientRect();
                          const scrollPercent = (e.clientY - rect.top) / rect.height;
                          dataPlansRef.current.scrollTo({
                            top: scrollPercent * maxScroll,
                            behavior: 'smooth',
                          });
                        }}
                        role="presentation"
                      >
                        <div
                          className="absolute left-0 w-full rounded-full bg-primary transition-all duration-150"
                          style={{
                            height:
                              maxScroll > 0
                                ? `${Math.max(20, (PLAN_GRID_HEIGHT_PX / (PLAN_GRID_HEIGHT_PX + maxScroll)) * 100)}%`
                                : '100%',
                            top:
                              maxScroll > 0
                                ? `${(scrollPosition / maxScroll) * (100 - (PLAN_GRID_HEIGHT_PX / (PLAN_GRID_HEIGHT_PX + maxScroll)) * 100)}%`
                                : '0%',
                          }}
                        />
                      </div>

                      <button
                        type="button"
                        aria-label="Scroll plans down"
                        onClick={() => {
                          dataPlansRef.current?.scrollBy({ top: 120, behavior: 'smooth' });
                        }}
                        className="flex h-6 w-6 items-center justify-center rounded text-primary transition-colors hover:bg-primary hover:text-white"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-gray-100 bg-brand-surface/60 p-4 lg:hidden">
              <div className="flex items-center justify-between text-sm">
                <span className="text-brand-muted">You pay</span>
                <span className="text-lg font-bold text-primary">
                  {!selectedPlan || quoteLoading || amountToPay == null
                    ? '₦0'
                    : `₦${amountToPay.toLocaleString()}`}
                </span>
              </div>
            </div>

            <Button
              fullWidth
              size="lg"
              className="py-3.5 text-base"
              disabled={!canPurchase}
              onClick={() => setShowPinModal(true)}
            >
              {selectedPlan && amountToPay != null
                ? `Buy Data · ₦${amountToPay.toLocaleString()}`
                : 'Buy Data · ₦0'}
            </Button>
          </div>
        </Card>

        <Card className="h-fit border-gray-200 p-5 shadow-card sm:p-6 lg:sticky lg:top-24 lg:p-6">
          <div className="mb-1 border-b border-gray-100 pb-3">
            <div className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-bold text-brand-navy">Order Summary</h2>
            </div>
            <p className="mt-1 text-xs text-brand-muted">Review your data purchase details</p>
          </div>

          <div className="divide-y divide-gray-100">
            <div className="flex items-center gap-3 py-3">
              <img src={selectedMeta.logo} alt={selectedMeta.label} className="h-10 w-10 object-contain" />
              <div>
                <p className="font-semibold text-brand-navy">{selectedMeta.label}</p>
                <p className="text-xs text-brand-muted">
                  Network{!networkManual && phoneNumber.length >= 4 ? ' · Auto-detected' : ''}
                </p>
              </div>
            </div>

            <div className="py-3">
              <p className="font-semibold text-brand-navy">{selectedPlanType || '—'}</p>
              <p className="text-xs text-brand-muted">Data type</p>
            </div>

            <div className="py-3">
              <div className="flex items-start gap-2">
                <Smartphone className="mt-0.5 h-4 w-4 shrink-0 text-brand-muted" />
                <div>
                  <p className="font-semibold text-brand-navy">
                    {selectedPlan?.planName ?? 'Not selected'}
                  </p>
                  <p className="text-xs text-brand-muted">
                    {selectedPlan?.validity
                      ? `${selectedPlan.validity} validity`
                      : 'Validity'}
                  </p>
                </div>
              </div>
            </div>

            <div className="py-3">
              <p className="font-semibold text-brand-navy">{phoneNumber || 'Not entered'}</p>
              <p className="text-xs text-brand-muted">Phone number</p>
            </div>

            <div className="flex items-end justify-between py-4">
              <div>
                <p className="font-semibold text-brand-navy">Total</p>
                <p className="text-xs text-brand-muted">Fixed price</p>
              </div>
              <p className="text-2xl font-bold text-primary">
                {!selectedPlan || quoteLoading || amountToPay == null
                  ? '₦0'
                  : `₦${amountToPay.toLocaleString()}`}
              </p>
            </div>
          </div>

          <div className="mt-3 rounded-xl bg-brand-surface px-4 py-3 text-sm">
            <div className="flex justify-between gap-3">
              <span className="text-brand-muted">Wallet balance</span>
              <span className="font-semibold text-brand-navy">₦{walletBalance.toLocaleString()}</span>
            </div>
          </div>
        </Card>
      </div>

      <PinVerificationModal
        open={showPinModal}
        onClose={() => setShowPinModal(false)}
        onConfirm={(pin) => void handlePurchase(pin)}
        loading={purchasing}
        title="Confirm data purchase"
        description={
          selectedPlan && amountToPay != null
            ? `Pay ₦${amountToPay.toLocaleString()} for ${selectedPlan.planName} ${selectedMeta.label} data.`
            : undefined
        }
      />
    </div>
  );
}
